import {
  isDeviceStatusLoading,
  useDeviceDataLoading,
} from 'features/Integrations/hooks/useDeviceStatusLoading';
import useWindow from 'hooks/useWindow';
import { useEffect, useMemo, useState } from 'react';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { BaseDevice } from 'types/devices.types';
import { getDeviceStatusCode } from 'utils/devices/icon.utils';
import type { Group } from '../features/ConnectedDeviceGroups/groups.types';
import IntegrationStatusBadge from './IntegrationStatusBadge';

/**
 * The badge exists to surface the group's worst condition, so a member that is merely
 * waiting for its data points must never outrank one that is alerting or flooding.
 * 'loading' is reported only when the group has nothing real to show yet: every member is
 * still loading, or the priority scan found nothing and would print the plain fallback.
 */
export function resolveGroupStatus(
  members: BaseDevice[],
  statusPriority: string[],
  isMemberDataLoading: boolean,
): string {
  // Nothing to scan: every `.some()` below is false on an empty list and `loadingCount` is 0,
  // so without this guard the fallback would report a healthy 'online' for a group whose
  // members are not known at all — the device list is still loading, or its request failed.
  // 'offline' is the same fallback the badge prints for a status it cannot resolve.
  if (members.length === 0) return isMemberDataLoading ? 'loading' : 'offline';

  // Find the highest priority status present among the members
  const status = statusPriority.find((priority) =>
    members.some((member) => getDeviceStatusCode(member).toLowerCase() === priority),
  );

  const loadingCount = members.filter((member) =>
    isDeviceStatusLoading(member, isMemberDataLoading),
  ).length;

  if (loadingCount > 0 && (!status || loadingCount === members.length)) return 'loading';

  return status || 'online';
}

export default function GroupDualStatus({ group }: { group: Group }) {
  // Fetch the connected devices list
  const {
    data: connectedDevices = [] as BaseDevice[],
    isLoading,
    isError,
  } = useGetConnectedDevicesQuery(undefined); // undefined here resolve type error

  const { width, height } = useWindow();

  // Deduplicate controller and device IDs
  const [controllerIds, setControllerIds] = useState<number[]>([]);
  const [deviceIds, setDeviceIds] = useState<number[]>([]);

  useEffect(() => {
    const uniqueControllerIds = Array.from(new Set(group.controllers));
    const uniqueDeviceIds = Array.from(new Set(group.devices));
    setControllerIds(uniqueControllerIds);
    setDeviceIds(uniqueDeviceIds);
  }, [group]);

  const hasControllers = controllerIds.length > 0;
  const hasDevices = deviceIds.length > 0;

  // Ask about this group's members only: a data fetch for devices elsewhere on the page
  // says nothing about these badges.
  const isControllerDataLoading = useDeviceDataLoading(controllerIds);
  const isDeviceDataLoading = useDeviceDataLoading(deviceIds);

  // Filter the connected devices to find those that match the controller and device IDs
  const controllers: BaseDevice[] = useMemo(
    () =>
      connectedDevices.filter((device: BaseDevice) => {
        return controllerIds.includes(device.id);
      }),
    [connectedDevices, controllerIds],
  );
  const devices: BaseDevice[] = useMemo(
    () =>
      connectedDevices.filter((device: BaseDevice) => {
        return deviceIds.includes(device.id);
      }),
    [connectedDevices, deviceIds],
  );

  // Status Priority arrays (highest to lowest)
  const controllerStatusPriority = [
    'major', // usgs
    'moderate', // usgs
    'minor', // usgs
    'action', //usgs
    'flooding', // stage
    'rising', // stage
    'dropping', // stage
    'warning', // stage
    'maintenance', // all
    'online', // all
  ];
  const deviceStatusPriority = [
    'alerting',
    'closed',
    'maintenance',
    'offline',
    'open',
    'idle',
    'online',
  ];

  const controllerStatus = useMemo(
    () => resolveGroupStatus(controllers, controllerStatusPriority, isControllerDataLoading),
    [controllers, isControllerDataLoading],
  );

  const deviceStatus = useMemo(
    () => resolveGroupStatus(devices, deviceStatusPriority, isDeviceDataLoading),
    [devices, isDeviceDataLoading],
  );

  return (
    <div
      className={`d-flex ${width >= 1400 ? 'flex-row gap-3' : 'flex-column gap-2'} align-items-center`}
    >
      {hasControllers && <IntegrationStatusBadge status={controllerStatus} />}
      {hasDevices && <IntegrationStatusBadge status={deviceStatus} />}
    </div>
  );
}
